import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fixStripeConnection } from "@lib/stripe";
import { fixRampConnection } from "@lib/ramp";
import { connectWithPlaid } from "@lib/plaidConnection";
import { integrationConnectionContractClient } from "./integrationConnection.api";
import { organizationIntegrationQueryKeys } from "./organizationIntegration.api";

/**
 * Org-scoped reconnects for the firm integrations table.
 *
 * Built on the raw `@lib/*` functions rather than the existing `Fix*` components
 * and `@mutations/*` hooks, all of which are workspace-scoped: they read
 * `useScope()`/`useConnectTarget()`, patch workspace query caches, and call
 * `invalidateAccountRelatedQueries({ workspaceId })`. They also take whole
 * `FinancialConnection`/`PlaidConnection` objects that a table row does not carry
 * and cannot be fabricated without casts.
 *
 * Everything here invalidates only the org integration queries — the firm table
 * is the sole consumer of these results.
 */
export function useOrgIntegrationReconnectActions(organizationId: string) {
    const queryClient = useQueryClient();

    const invalidate = useCallback(
        async () =>
            await queryClient.invalidateQueries({
                queryKey: organizationIntegrationQueryKeys.all(organizationId),
            }),
        [queryClient, organizationId],
    );

    /**
     * Plaid resolves `{ connected }` rather than throwing when the user abandons
     * Link, so a dismissed dialog must not read as a successful reconnect.
     */
    const reconnectPlaid = useCallback(
        async (plaidConnectionId: number) => {
            const { connected } = await connectWithPlaid({
                connectionId: plaidConnectionId,
                organizationId,
            });
            if (connected) {
                await invalidate();
            }
            return connected;
        },
        [invalidate, organizationId],
    );

    const reconnectStripe = useCallback(
        async ({
            connectionId,
            code,
        }: {
            connectionId: number;
            code: string;
        }) => {
            await fixStripeConnection(connectionId, { code });
            await invalidate();
            return true;
        },
        [invalidate],
    );

    const reconnectRamp = useCallback(
        async ({
            connectionId,
            code,
        }: {
            connectionId: number;
            code: string;
        }) => {
            await fixRampConnection(connectionId, { code });
            await invalidate();
            return true;
        },
        [invalidate],
    );

    /**
     * The only workspace-scoped path: the native fix endpoint sits behind
     * `WorkspaceAccessGuard`, so the caller has to resolve the client's workspace
     * off the row first.
     */
    const reconnectNative = useCallback(
        async ({
            workspaceId,
            connectionId,
            oAuthCode,
            customParams,
        }: {
            workspaceId: string;
            connectionId: string;
            oAuthCode?: string;
            customParams?: { authCode: string; sharedId: string };
        }) => {
            await integrationConnectionContractClient.fixConnection({
                params: { workspaceId, connectionId },
                body: { oAuthCode, customParams },
            });
            await invalidate();
            return true;
        },
        [invalidate],
    );

    return {
        reconnectPlaid,
        reconnectStripe,
        reconnectRamp,
        reconnectNative,
    };
}
